/**
 * V4.35 / V4.40 — Moteur de relances Email contextuel.
 *
 * 19 templates par audience (Client 8 / Banque 4 / BOS 3 / Apporteur 4).
 * Le RESOLVER sélectionne les templates compatibles avec la SITUATION RÉELLE du
 * dossier (étape, statut, présence de RDV, retard, etc.) et marque le plus
 * pertinent SUGGÉRÉ.
 *
 * Architecture 3 couches (compatible IA V2) :
 *   1. TEMPLATES — catalogue fixe
 *   2. RESOLVER  — context-aware filtering + ordering
 *   3. BUILDER   — produit {to, subject, body}
 */

const FALLBACK_SIGNATURE = "Teddy Fortassier";

function signature(user) {
  return (user?.name || "").trim() || FALLBACK_SIGNATURE;
}

function clientDisplayName(dossier) {
  return (
    dossier?.client_name ||
    dossier?.nom ||
    [dossier?.client_1_prenom, dossier?.client_1_nom].filter(Boolean).join(" ").trim() ||
    "votre dossier"
  );
}

const STAGE_LABEL_FR = {
  prospect: "Prospect",
  compromis: "Compromis signé",
  envoi_banque: "En banque",
  accord_banque: "Accord banque",
  accord_risque: "Accord risque obtenu",
  rdv_bancaire: "Rendez-vous bancaire",
  offre_editee: "Offre éditée",
  acte_authentique: "Acte authentique",
};

function stageLabel(dossier) {
  return STAGE_LABEL_FR[dossier?.etape] || dossier?.etape || "—";
}

// Helpers contexte
const isAtOrPast = (dossier, stage) => {
  const order = ["prospect","compromis","envoi_banque","accord_banque","accord_risque","rdv_bancaire","offre_editee","acte_authentique"];
  return order.indexOf(dossier?.etape) >= order.indexOf(stage);
};

// ---------------------------------------------------------------------------
// CATALOGUE de templates (19)
// ---------------------------------------------------------------------------

const T = {
  // === CLIENT (10) ========================================================
  // V4.48 — Prise de contact (contact_status = a_appeler)
  client_prise_contact: {
    id: "client_prise_contact", label: "Prise de contact", audience: "client",
    description: "Premier contact / qualification du besoin",
    build: (d, u) => ({
      subject: `Votre projet immobilier — ${clientDisplayName(d)}`,
      body: `Bonjour,

Je reviens vers vous concernant votre projet immobilier. Auriez-vous quelques minutes à me consacrer pour que nous puissions échanger sur votre besoin et identifier ensemble la meilleure solution de financement ?

Je reste disponible pour un premier échange téléphonique à votre convenance.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_rdv_prise: {
    id: "client_rdv_prise", label: "Proposition de rendez-vous", audience: "client",
    description: "Proposer un premier RDV (uniquement si RDV pas encore fait)",
    build: (d, u) => ({
      subject: "Prise de rendez-vous — Votre projet immobilier",
      body: `Bonjour,

Je reviens vers vous concernant votre projet immobilier. Pouvons-nous fixer un rendez-vous afin d'échanger sur votre besoin et identifier ensemble la meilleure solution de financement ?

Merci de m'indiquer vos disponibilités sur les prochains jours.

Bien cordialement,

${signature(u)}`,
    }),
  },
  // V4.48 — Suite à notre rendez-vous (contact_status = rdv_fait)
  client_suite_rdv: {
    id: "client_suite_rdv", label: "Suite à notre rendez-vous", audience: "client",
    description: "Point projet après un RDV déjà fait",
    build: (d, u) => ({
      subject: `Suite à notre rendez-vous — ${clientDisplayName(d)}`,
      body: `Bonjour,

Je fais suite à notre rendez-vous concernant votre projet immobilier.

Je souhaitais faire un point avec vous sur l'avancement du projet et vérifier si vous avez pu avancer de votre côté.

Pouvez-vous me tenir informé de la situation actuelle ?

Je reste disponible si besoin.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_rdv_confirm: {
    id: "client_rdv_confirm", label: "Confirmation de rendez-vous", audience: "client",
    description: "Confirmer un RDV à venir",
    build: (d, u) => ({
      subject: "Confirmation de notre rendez-vous",
      body: `Bonjour,

Petit message pour vous confirmer notre rendez-vous concernant votre projet immobilier.

Si vous avez la moindre question avant notre échange, n'hésitez pas à me solliciter.

À très bientôt.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_pieces_manquantes: {
    id: "client_pieces_manquantes", label: "Pièces manquantes", audience: "client",
    description: "Documents complémentaires à demander",
    build: (d, u) => ({
      subject: `Dossier ${clientDisplayName(d)} — Documents complémentaires`,
      body: `Bonjour,

Afin de poursuivre l'étude de votre financement, il me manque encore certains éléments concernant votre dossier.

Pouvez-vous me transmettre les documents restants dès que possible afin que je puisse poursuivre les démarches auprès des partenaires bancaires ?

Je reste à votre disposition.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_compromis_attendu: {
    id: "client_compromis_attendu", label: "Compromis attendu", audience: "client",
    description: "Relance signature du compromis chez le notaire",
    build: (d, u) => ({
      subject: `Dossier ${clientDisplayName(d)} — Signature du compromis`,
      body: `Bonjour,

Pouvez-vous me confirmer la date prévue pour la signature du compromis chez le notaire ?

Une fois le compromis signé, je pourrai envoyer le dossier complet aux banques partenaires pour étude.

Je reste à votre disposition.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_envoye_banque: {
    id: "client_envoye_banque", label: "Dossier envoyé en banque", audience: "client",
    description: "Information : dossier en cours d'étude bancaire",
    build: (d, u) => ({
      subject: `Dossier ${clientDisplayName(d)} — Envoi en banque`,
      body: `Bonjour,

Petit point d'avancement : votre dossier a été transmis aux partenaires bancaires pour étude.

Je vous tiens informé(e) dès que j'obtiens un retour. Délai moyen de retour : 2 à 3 semaines selon la banque.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_offre_a_signer: {
    id: "client_offre_a_signer", label: "Offre éditée à signer", audience: "client",
    description: "Relance signature de l'offre de prêt",
    build: (d, u) => ({
      subject: `Dossier ${clientDisplayName(d)} — Signature de l'offre`,
      body: `Bonjour,

Je reviens vers vous concernant votre offre de prêt.

Pouvez-vous me confirmer que la signature a bien été réalisée ou m'indiquer où vous en êtes dans vos démarches ?

Je reste à votre disposition si besoin.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_signature_demande: {
    id: "client_signature_demande", label: "Signature réalisée ?", audience: "client",
    description: "Confirmation post-signature offre",
    build: (d, u) => ({
      subject: `Dossier ${clientDisplayName(d)} — Confirmation de signature`,
      body: `Bonjour,

Pouvez-vous me confirmer que la signature de l'offre a bien été envoyée à la banque ?

Une fois le retour signé reçu par la banque, nous pourrons fixer la date de l'acte authentique chez le notaire.

Bien cordialement,

${signature(u)}`,
    }),
  },
  client_relance_generale: {
    id: "client_relance_generale", label: "Relance générale", audience: "client",
    description: "Relance neutre pour reprendre contact",
    build: (d, u) => ({
      subject: `Suivi de votre dossier ${clientDisplayName(d)}`,
      body: `Bonjour,

Je me permets de revenir vers vous concernant votre projet immobilier.

Pouvez-vous me faire un point sur votre situation actuelle ? Je reste disponible si vous avez la moindre question.

Bien cordialement,

${signature(u)}`,
    }),
  },

  // === BANQUE (4) =========================================================
  banque_etude: {
    id: "banque_etude", label: "Étude du dossier", audience: "banque",
    description: "Demande d'état d'avancement de l'étude",
    build: (d, u) => ({
      subject: `Suivi dossier ${clientDisplayName(d)}`,
      body: `Bonjour,

Je me permets de revenir vers vous concernant le dossier de ${clientDisplayName(d)} envoyé pour étude.

Pouvez-vous me confirmer son état d'avancement et la date prévisionnelle de retour ?

Merci par avance.

Bien cordialement,

${signature(u)}`,
    }),
  },
  banque_accord_attendu: {
    id: "banque_accord_attendu", label: "Accord attendu", audience: "banque",
    description: "Relance pour obtenir l'accord de principe",
    build: (d, u) => ({
      subject: `Demande d'accord — Dossier ${clientDisplayName(d)}`,
      body: `Bonjour,

Je reviens vers vous concernant le dossier ${clientDisplayName(d)}.

Avez-vous des éléments à me communiquer sur la décision de la banque (accord, ajustement, demande complémentaire) ?

Merci pour votre retour.

Bien cordialement,

${signature(u)}`,
    }),
  },
  banque_offre_attendue: {
    id: "banque_offre_attendue", label: "Offre attendue", audience: "banque",
    description: "Relance édition de l'offre de prêt",
    build: (d, u) => ({
      subject: `Édition de l'offre — Dossier ${clientDisplayName(d)}`,
      body: `Bonjour,

Suite à l'accord obtenu sur le dossier ${clientDisplayName(d)}, je reviens vers vous concernant l'édition de l'offre de prêt.

Avez-vous une visibilité sur la date prévisionnelle d'émission ?

Merci d'avance.

Bien cordialement,

${signature(u)}`,
    }),
  },
  banque_urgent: {
    id: "banque_urgent", label: "Relance urgente", audience: "banque",
    description: "Relance urgente (retard sur cible)",
    build: (d, u) => ({
      subject: `URGENT — Dossier ${clientDisplayName(d)}`,
      body: `Bonjour,

Je me permets de relancer concernant le dossier ${clientDisplayName(d)}, actuellement à l'étape : ${stageLabel(d)}.

Le délai cible étant dépassé, pouvez-vous me confirmer où nous en sommes et me donner une estimation du prochain retour ?

Merci pour votre attention sur ce dossier.

Bien cordialement,

${signature(u)}`,
    }),
  },

  // === BOS (3) ============================================================
  bos_signature_client: {
    id: "bos_signature_client", label: "Signature client", audience: "bos",
    description: "Point sur le retour signé de l'offre",
    build: (d, u) => ({
      subject: `Signature offre — ${clientDisplayName(d)}`,
      body: `Bonjour,

Peux-tu me faire un point sur le retour signé de l'offre concernant ${clientDisplayName(d)} ?

- Offre signée par le client ?
- Retournée à la banque ?
- Délai estimé ?

Merci.

${signature(u)}`,
    }),
  },
  bos_retour_notaire: {
    id: "bos_retour_notaire", label: "Retour notaire", audience: "bos",
    description: "Suivi du retour notaire et date de l'acte",
    build: (d, u) => ({
      subject: `Retour notaire — ${clientDisplayName(d)}`,
      body: `Bonjour,

Peux-tu me confirmer où nous en sommes côté notaire pour le dossier ${clientDisplayName(d)} ?

- Date prévisionnelle de l'acte ?
- Tous les éléments en main ?
- Point bloquant éventuel ?

Merci.

${signature(u)}`,
    }),
  },
  bos_acte_authentique: {
    id: "bos_acte_authentique", label: "Acte authentique", audience: "bos",
    description: "Confirmation date et déroulement de l'acte",
    build: (d, u) => ({
      subject: `Acte authentique — ${clientDisplayName(d)}`,
      body: `Bonjour,

Peux-tu confirmer la date définitive de l'acte authentique pour ${clientDisplayName(d)} et m'indiquer si tout est en place ?

Merci.

${signature(u)}`,
    }),
  },

  // === APPORTEUR (4) ======================================================
  apporteur_info_avancement: {
    id: "apporteur_info_avancement", label: "Information d'avancement", audience: "apporteur",
    description: "Point d'avancement générique",
    build: (d, u) => ({
      subject: `Point avancement — ${clientDisplayName(d)}`,
      body: `Bonjour,

Petit point d'avancement concernant le dossier de ${clientDisplayName(d)}.

Étape actuelle : ${stageLabel(d)}.

Je te tiens informé(e) de la suite.

À bientôt.

${signature(u)}`,
    }),
  },
  apporteur_compromis_signe: {
    id: "apporteur_compromis_signe", label: "Compromis signé", audience: "apporteur",
    description: "Info compromis signé, dossier en route",
    build: (d, u) => ({
      subject: `Compromis signé — ${clientDisplayName(d)}`,
      body: `Bonjour,

Bonne nouvelle : le compromis de ${clientDisplayName(d)} est signé. Le dossier va être transmis aux banques partenaires pour étude.

Je reviens vers toi dès que j'ai un retour banque.

À bientôt.

${signature(u)}`,
    }),
  },
  apporteur_accord_obtenu: {
    id: "apporteur_accord_obtenu", label: "Accord obtenu", audience: "apporteur",
    description: "Info accord banque obtenu",
    build: (d, u) => ({
      subject: `Accord banque obtenu — ${clientDisplayName(d)}`,
      body: `Bonjour,

Le dossier de ${clientDisplayName(d)} a obtenu l'accord de la banque.

Prochaines étapes : édition de l'offre puis signature client.

Je te tiens informé(e).

À bientôt.

${signature(u)}`,
    }),
  },
  apporteur_acte_signe: {
    id: "apporteur_acte_signe", label: "Acte signé", audience: "apporteur",
    description: "Info acte authentique signé",
    build: (d, u) => ({
      subject: `Acte signé — ${clientDisplayName(d)}`,
      body: `Bonjour,

Le dossier de ${clientDisplayName(d)} a été signé chez le notaire. Tout s'est bien passé.

Merci encore pour cette mise en relation.

À très vite.

${signature(u)}`,
    }),
  },
};

// ---------------------------------------------------------------------------
// RESOLVER contextuel : sélectionne et ordonne les templates selon la
// situation réelle du dossier (étape + statut + dates clés + retard).
// Le premier template renvoyé est SUGGÉRÉ dans l'UI.
// ---------------------------------------------------------------------------

export function getCompatibleTemplates(audience, dossier) {
  const etape = dossier?.etape;
  // V4.48 — Le contact_status est LA source de vérité pour choisir le bon template
  // client. On ne propose JAMAIS "prendre RDV" si un RDV a déjà été fait.
  const contactStatus = dossier?.contact_status || dossier?.type_contact;
  // hasRdvFait = signal explicite qu'un rendez-vous a déjà eu lieu
  const hasRdvFait =
    contactStatus === "rdv_fait" ||
    !!dossier?.rdv_fait_date ||
    !!dossier?.etape_dates?.rdv_fait;
  const hasCompromis = !!dossier?.compromis_date || !!dossier?.etape_dates?.compromis;
  const isOverdue = (dossier?.retard_days || 0) > 0;

  if (audience === "client") {
    const list = [];
    // ═══ Étapes bancaires : le template banque prime sur le contact_status ═══
    if (etape === "envoi_banque" || etape === "accord_banque" || etape === "accord_risque") {
      list.push(T.client_envoye_banque);
      list.push(T.client_pieces_manquantes);
      if (hasRdvFait) list.push(T.client_suite_rdv);
      list.push(T.client_relance_generale);
      return dedupe(list);
    }
    if (etape === "rdv_bancaire") {
      list.push(T.client_rdv_confirm);
      list.push(T.client_envoye_banque);
      list.push(T.client_relance_generale);
      return dedupe(list);
    }
    if (etape === "offre_editee") {
      list.push(T.client_offre_a_signer);
      list.push(T.client_signature_demande);
      list.push(T.client_relance_generale);
      return dedupe(list);
    }
    if (etape === "acte_authentique") {
      list.push(T.client_signature_demande);
      list.push(T.client_relance_generale);
      return dedupe(list);
    }

    // ═══ Étapes amont (prospect/compromis) : logique contact_status prime ═══
    // Règle stricte user : ne JAMAIS proposer "Proposition de RDV" si RDV déjà fait.
    if (hasRdvFait) {
      // Client déjà rencontré → Suite RDV en premier
      list.push(T.client_suite_rdv);
      if (!hasCompromis) list.push(T.client_compromis_attendu);
      list.push(T.client_pieces_manquantes);
      list.push(T.client_relance_generale);
      return dedupe(list);
    }
    if (contactStatus === "a_appeler") {
      // Pas encore contacté → prise de contact / qualification
      list.push(T.client_prise_contact);
      list.push(T.client_relance_generale);
      return dedupe(list);
    }
    if (contactStatus === "rdv_a_faire") {
      // RDV à planifier → proposition RDV
      list.push(T.client_rdv_prise);
      list.push(T.client_prise_contact);
      list.push(T.client_relance_generale);
      return dedupe(list);
    }

    // ═══ Fallback : pas de contact_status renseigné et pas de RDV connu ═══
    // On propose la prise de contact par défaut (moins invasif qu'une proposition RDV)
    list.push(T.client_prise_contact);
    if (!hasCompromis) list.push(T.client_compromis_attendu);
    list.push(T.client_pieces_manquantes);
    list.push(T.client_relance_generale);
    return dedupe(list);
  }

  if (audience === "banque") {
    const list = [];
    if (isOverdue) list.push(T.banque_urgent);
    if (etape === "envoi_banque") {
      list.push(T.banque_etude);
      list.push(T.banque_accord_attendu);
    } else if (etape === "accord_banque" || etape === "accord_risque" || etape === "rdv_bancaire") {
      list.push(T.banque_offre_attendue);
      list.push(T.banque_accord_attendu);
    } else {
      list.push(T.banque_etude);
      list.push(T.banque_accord_attendu);
      list.push(T.banque_offre_attendue);
    }
    if (!isOverdue) list.push(T.banque_urgent);
    return dedupe(list);
  }

  if (audience === "bos") {
    const list = [];
    const act = dossier?.bos_next_action;
    if (act === "wait_notaire") list.push(T.bos_retour_notaire);
    else if (act === "wait_acte") list.push(T.bos_acte_authentique);
    else list.push(T.bos_signature_client);
    list.push(T.bos_retour_notaire);
    list.push(T.bos_acte_authentique);
    list.push(T.bos_signature_client);
    return dedupe(list);
  }

  if (audience === "apporteur") {
    const list = [];
    if (etape === "compromis" || isAtOrPast(dossier, "compromis")) {
      if (etape === "acte_authentique" || dossier?.status === "signe") list.push(T.apporteur_acte_signe);
      else if (["accord_banque","accord_risque","rdv_bancaire","offre_editee"].includes(etape)) list.push(T.apporteur_accord_obtenu);
      else list.push(T.apporteur_compromis_signe);
    } else {
      list.push(T.apporteur_info_avancement);
    }
    list.push(T.apporteur_info_avancement);
    return dedupe(list);
  }

  return Object.values(T);
}

function dedupe(arr) {
  const seen = new Set();
  return arr.filter((t) => {
    if (seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });
}

// ---------------------------------------------------------------------------
// BUILDER — produit {to, subject, body} prêt à injecter dans Gmail.
// ---------------------------------------------------------------------------

export function buildEmail(template, dossier, user, audience) {
  const { subject, body } = template.build(dossier || {}, user || {});
  const to = audience === "client" ? (dossier?.client_email || "") : "";
  return { to, subject, body };
}

// ---------------------------------------------------------------------------
// GMAIL OPENER
// ---------------------------------------------------------------------------

export function openGmailDraft({ to, subject, body }) {
  const params = new URLSearchParams();
  params.set("view", "cm");
  params.set("fs", "1");
  if (to) params.set("to", to);
  if (subject) params.set("su", subject);
  if (body) params.set("body", body);
  const url = `https://mail.google.com/mail/?${params.toString()}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

// ---------------------------------------------------------------------------
// Point d'extension future IA (V2)
// Pour brancher une génération IA, remplacer buildEmail() par un appel async
// au backend qui retourne le même contrat { to, subject, body }.
// ---------------------------------------------------------------------------

export const EMAIL_TEMPLATES = T;
